/**
 * lookups.js — load & present the CRM's lookup lists (customer statuses, tags)
 * for Ant <Select>s. Replaces the old ListPicker autocomplete in migrated screens.
 *
 * Data source is unchanged: the same `getDISTINCTListFromDB('*', [{listName}], listName)`
 * Redux action ListPicker used (POST /api/getDISTINCTListFromDB). Items look like
 * { id, name, val, color? } — `val` is a legacy alias of `name` added by the server.
 *
 * Exports:
 *   LIST.STATUS / LIST.TAG        list names used by the API
 *   useLookupList(listName)       hook → { items, loading, reload }
 *   itemLabel(item)               display label (same rule as ListPicker: text before the first comma)
 *   toOptions(items)              → [{ value: id, label, item }] for <Select options>
 *   statusColor(idOrName)         stable soft color for a status (statuses have no color column)
 */
import { useEffect, useState, useCallback } from 'react';
import { getDISTINCTListFromDB } from '../actions/generalActions.js';

export const LIST = {
  STATUS: 'customerStatus',
  TAG: 'eventsType',
};

// The action is a redux-thunk (`(...) => async dispatch => res.data`) that never dispatches,
// so we can run it directly with a no-op dispatch and keep using the exact same request.
const noopDispatch = () => {};
export const fetchLookupList = (listName) =>
  getDISTINCTListFromDB('*', [{ key: 'listName', val: [listName] }], listName)(noopDispatch);

export const useLookupList = (listName) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchLookupList(listName);
      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(`Failed to load list ${listName}:`, e);
      setItems([]);
    }
    setLoading(false);
  }, [listName]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { items, loading, reload };
};

export const itemLabel = (item) => {
  if (item === null || item === undefined) return '';
  const label = typeof item === 'object' ? item.val || item.name || '' : item;
  return String(label).split(',')[0].trim();
};

export const toOptions = (items) =>
  (items || []).map((item) => ({ value: item.id, label: itemLabel(item), item }));

// Calm palette for things that have no stored color (statuses).
const PALETTE = ['#5B5BD6', '#0EA5E9', '#16A34A', '#D97706', '#DB2777', '#7C3AED', '#0D9488', '#EA580C', '#64748B'];

export const statusColor = (key) => {
  if (key === null || key === undefined || key === '') return '#71717A';
  const s = String(key);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length];
};
